import { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from "react";
import { BiDownArrow, BiSearchAlt, BiUpArrow } from "react-icons/bi";
import { FaAngleDown, FaAngleUp } from "react-icons/fa";
import { TiTimes } from "react-icons/ti";

const normalize = (s) =>
  (s ?? "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "") // bỏ dấu tiếng Việt
    .toLowerCase();

const getLabel = (item) =>
  typeof item === "string" ? item : item?.label ?? "";

const Dropdown = ({
  placeholder = "Select...",
  items = [],
  onSelect,
  className = "",
  icon,
  value,
  isSetSelect = true,
  loading = false,

  // 🔎 Search props
  searchable = true,
  searchPlaceholder = "Tìm kiếm...",
  noResultsText = "Không có kết quả",
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  // giữ đồng bộ khi value prop thay đổi
  useEffect(() => {
    setSelected(value);
  }, [value]);

  // Click ngoài đóng menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus input khi mở
  useEffect(() => {
    if (open && searchable) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      // đóng thì reset lựa chọn điều hướng
      setActiveIndex(0);
    }
  }, [open, searchable]);

  // Tính minWidth menu theo text dài nhất & theo width nút
  useEffect(() => {
    if (!menuRef.current || !buttonRef.current) return;
    const ctx = document.createElement("canvas").getContext("2d");
    const style = window.getComputedStyle(buttonRef.current);
    ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

    let maxWidth = 0;
    const candidates = items?.map(getLabel).concat([placeholder, searchPlaceholder]);
    candidates.forEach((text) => {
      maxWidth = Math.max(maxWidth, ctx.measureText(text).width);
    });

    const buttonW = buttonRef.current.offsetWidth || 0;
    const minW = Math.max(Math.ceil(maxWidth) + 32, buttonW, 200); // padding + tối thiểu
    menuRef.current.style.minWidth = `${minW}px`;
  }, [items, placeholder, searchPlaceholder]);

  const filteredItems = useMemo(() => {
    if (!searchable || !query.trim()) return items;
    const q = normalize(query);
    return items.filter((it) => normalize(getLabel(it)).includes(q));
  }, [items, query, searchable]);

  // Đảm bảo activeIndex nằm trong range sau khi lọc
  useEffect(() => {
    if (activeIndex >= filteredItems.length) setActiveIndex(0);
  }, [filteredItems, activeIndex]);

  const doSelect = useCallback(
    (item) => {
      if (isSetSelect) setSelected(item);
      onSelect?.(item);
      setOpen(false);
      setQuery(""); // clear query sau chọn
    },
    [isSetSelect, onSelect]
  );

  const onKeyDown = (e) => {
    if (!open) return;
    if (!filteredItems.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % filteredItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + filteredItems.length) % filteredItems.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      doSelect(filteredItems[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };
  const [menuInnerStyle, setMenuInnerStyle] = useState({});

  // clamp ngang khi mở
  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
  
    const PADDING = 8;
  
    const compute = () => {
      // đo khung menu (outer) sau khi mở
      const rect = menuRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
  
      let dx = 0;
      if (rect.left < PADDING) {
        dx = PADDING - rect.left;             // dịch vào phải
      } else if (rect.right > vw - PADDING) {
        dx = -(rect.right - (vw - PADDING));  // dịch vào trái
      }
      setMenuInnerStyle({
        left: `${dx}px`,                // ⬅️ áp cho inner
       
      });
    };
  
    // cần đợi frame mở (scale/opacity) xong mới đo
    requestAnimationFrame(compute);
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      setMenuInnerStyle({
       
       
      });
      window.removeEventListener("resize", compute);
     window.removeEventListener("scroll", compute, true);
    };
  }, [open]);
  return (
    <div className="relative inline-block text-left text-xs xl:text-sm w-full">
      <button
        type="button"
        ref={buttonRef}
        onClick={() => !loading && setOpen((o) => !o)}
        className={`inline-flex gap-2 justify-between items-center w-full rounded-md px-2 py-2 text-xs xl:text-sm font-medium ${className}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {icon}
        <span className="flex-1 truncate text-left">
          {!isSetSelect && placeholder}
          {isSetSelect &&
            (selected
              ? typeof selected === "string"
                ? selected
                : selected.label
              : placeholder)}
        </span>

        {loading ? (
          <span className="ml-2 text-gray-400 animate-pulse">...</span>
        ) : !open ? (
          <FaAngleDown />
        ) : (
          <FaAngleUp />
        )}
      </button>

      <div
        ref={menuRef}
        style={menuInnerStyle}
        className={`absolute right-0 mt-2 rounded-md shadow-lg bg-white ring-1 left-0 w-full ring-black ring-opacity-5 z-10 transform transition-all duration-200 origin-top
        ${open ? "scale-y-100 opacity-100" : "scale-y-0 opacity-0 pointer-events-none"}`}
        onKeyDown={onKeyDown}
      >
        {/* ô tìm kiếm */}
        {searchable && (
          <div className="sticky top-0 bg-white border-b px-2 py-2">
            <div className="relative">
              <BiSearchAlt className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full border rounded-md pl-8 pr-7 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {query && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    setQuery("");
                    setActiveIndex(0);
                    inputRef.current?.focus();
                  }}
                  aria-label="Xoá tìm kiếm"
                >
                  <TiTimes />
                </button>
              )}
            </div>
          </div>
        )}

        {/* danh sách */}
        <div className="py-1 max-h-60 overflow-y-auto" role="listbox">
          {loading ? (
            <div className="px-4 py-2 text-gray-500 text-sm">Đang tải...</div>
          ) : filteredItems.length === 0 ? (
            <div className="px-4 py-2 text-gray-500 text-sm">{noResultsText}</div>
          ) : (
            filteredItems?.map((item, idx) => {
              const labelText = getLabel(item);
              const isActive = idx === activeIndex;
              return (
                <button
                  type="button"
                  key={`${labelText}-${idx}`}
                  onClick={() => doSelect(item)}
                  onMouseEnter={() => setActiveIndex(idx)}
                  className={`w-full text-left px-4 py-2 transition text-xs xl:text-sm break-words ${
                    isActive ? "bg-gray-100" : "hover:bg-gray-50"
                  }`}
                  role="option"
                  aria-selected={isActive}
                >
                  {labelText}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Dropdown;
